import type { i18n } from 'i18next';

export function formatDuration(minutes: number | null, i18n: i18n): string | null {
    if (minutes === null || minutes === undefined) return null;

    const totalHours = Math.floor(minutes / 60);
    const totalMinutes = minutes % 60;

    const formattedHours = i18n.t(
        'shared.admin.workOrder.task.auditLog.taskTime.hours-count',
        { count: totalHours }
    );
    const formattedMinutes = i18n.t(
        'shared.admin.workOrder.task.auditLog.taskTime.minutes-count',
        { count: totalMinutes }
    );

    if (totalHours && totalMinutes) {
        return `${formattedHours} ${formattedMinutes}`;
    }

    if (totalMinutes) {
        return formattedMinutes;
    }

    return formattedHours;
}

export function formatFriendlyDuration(minutes: number | null, i18n: i18n, defaultReturnValue: string = '-'): string {
  if (typeof minutes !== 'number' || Number.isNaN(minutes)) {
    return defaultReturnValue;
  }

  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  let result = '';

  if (h > 0) {
    result = i18n.t(
    'shared.admin.workOrder.serviceRequest.hours-count-unit-symbol-format',
      { count: h }
    );
  }

  if (m > 0) {
    const formatedMinutes = i18n.t(
      'shared.admin.workOrder.serviceRequest.minutes-count-unit-symbol-format',
      { count: m }
    );

    result += (result ? ' ' : '') + formatedMinutes;
  }

  return result || defaultReturnValue;
}

export function convertMinutesToHoursAndMinutes(totalMinutes: number | null) {
  if (totalMinutes === null) {
    return { hours: null, minutes: null };
  }

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return { hours, minutes };
}

export const calculateTotalMinutes = (hours: number | null, minutes: number | null) => {
  const hoursValue = hours ?? 0;
  const minutesValue = minutes ?? 0;

  return hoursValue * 60 + minutesValue;
};

/**
 * Formats milliseconds to a human-readable duration string using the largest applicable unit
 * Examples: "5 minutes", "2 hours", "1 week", "3 days"
 * Uses i18n interpolation for proper internationalization
 */
export function formatDurationFromMilliseconds(ms: number, i18n: i18n): string {
  const ONE_MINUTE = 60 * 1000;
  const ONE_HOUR = 60 * ONE_MINUTE;
  const ONE_DAY = 24 * ONE_HOUR;
  const ONE_WEEK = 7 * ONE_DAY;

  if (ms >= ONE_WEEK) {
    const weeks = Math.floor(ms / ONE_WEEK);

    return i18n.t('shared.timeUnits.weeks-count', {
      count: weeks,
    });
  }

  if (ms >= ONE_DAY) {
    const days = Math.floor(ms / ONE_DAY);

    return i18n.t('shared.timeUnits.days-count', {
      count: days,
    });
  }

  if (ms >= ONE_HOUR) {
    const hours = Math.floor(ms / ONE_HOUR);

    return i18n.t('shared.timeUnits.hours-count', {
      count: hours,
    });
  }

  const minutes = Math.floor(ms / ONE_MINUTE);

  return i18n.t('shared.timeUnits.minutes-count', {
    count: minutes,
  });
}
